import { type KeyboardEvent } from "react";
import { Card } from "./Card.js";
import { Icon } from "./Icon.js";
import type { QuizQuestion, QuizResult } from "../lib/api.js";

/** Pass mark for comprehension quizzes (shared by /learning and the 閲覧者 reader). */
export const PASS_THRESHOLD = 80;

/** Compute the scored result from the answer selections. */
export function scoreQuiz(questions: QuizQuestion[], answers: Record<number, number>): QuizResult {
  const total = questions.length;
  let correct = 0;
  questions.forEach((q, i) => {
    if (answers[i] === q.answerIndex) correct += 1;
  });
  const score = total ? Math.round((correct / total) * 100) : 0;
  return { correct, total, score, passed: score >= PASS_THRESHOLD };
}

/**
 * One question with 4 radio-style choices, immediate correct/incorrect feedback + explanation.
 * Token-only styling so it themes to BOTH surfaces (console light / field dark) for free.
 * Shared by Learning (managers) and the 閲覧者 Reader. `onReview` (optional) renders a
 * "手順N を見直す" deep-link in the explanation when the question maps to a step.
 */
export function QuizCard({
  index,
  question,
  selected,
  onAnswer,
  onReview,
}: {
  index: number;
  question: QuizQuestion;
  selected: number | undefined;
  onAnswer: (choiceIndex: number) => void;
  onReview?: (stepSeq: number) => void;
}) {
  const answered = selected != null;
  const correct = answered && selected === question.answerIndex;

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (answered) return;
    const n = question.choices.length;
    let next: number | null = null;
    if (e.key === "ArrowDown" || e.key === "ArrowRight") next = ((selected ?? -1) + 1 + n) % n;
    else if (e.key === "ArrowUp" || e.key === "ArrowLeft") next = ((selected ?? 0) - 1 + n) % n;
    else return;
    e.preventDefault();
    onAnswer(next);
  }

  return (
    <Card pad role="listitem">
      <div className="mono" style={{ fontSize: 11, color: "var(--muted)" }}>
        Q{index + 1}
        {question.stepSeq != null ? ` · 手順${question.stepSeq}` : ""}
      </div>
      <div className="display jp" style={{ fontSize: 18, margin: "6px 0 12px" }}>
        {question.question}
      </div>
      <div
        role="radiogroup"
        aria-label={`Q${index + 1} の選択肢`}
        onKeyDown={onKeyDown}
        style={{ display: "grid", gap: 8 }}
      >
        {question.choices.map((choice, ci) => {
          const isSelected = selected === ci;
          const isAnswer = ci === question.answerIndex;
          // After answering: highlight the correct choice (green) and a wrong pick (red).
          const showCorrect = answered && isAnswer;
          const showWrong = answered && isSelected && !isAnswer;
          const borderColor = showCorrect
            ? "var(--ok)"
            : showWrong
              ? "var(--danger)"
              : isSelected
                ? "var(--ink)"
                : "var(--line)";
          const bg = showCorrect
            ? "color-mix(in srgb,var(--ok) 14%,var(--surface))"
            : showWrong
              ? "color-mix(in srgb,var(--danger) 12%,var(--surface))"
              : "var(--surface)";
          return (
            <button
              key={ci}
              type="button"
              role="radio"
              aria-checked={isSelected}
              tabIndex={answered ? -1 : isSelected || (selected == null && ci === 0) ? 0 : -1}
              disabled={answered}
              onClick={() => onAnswer(ci)}
              className="jp"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                textAlign: "left",
                padding: "12px 14px",
                borderRadius: 12,
                border: `1px solid ${borderColor}`,
                background: bg,
                color: "var(--ink)",
                font: "inherit",
                minHeight: 56,
                cursor: answered ? "default" : "pointer",
                transition: "border-color .15s, background .15s",
              }}
            >
              <span
                aria-hidden="true"
                className="mono"
                style={{
                  flex: "0 0 auto",
                  width: 24,
                  height: 24,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: "50%",
                  border: `1px solid ${borderColor}`,
                  fontSize: 12,
                }}
              >
                {showCorrect ? (
                  <Icon name="check" size={14} />
                ) : showWrong ? (
                  <Icon name="close" size={14} />
                ) : (
                  String.fromCharCode(65 + ci)
                )}
              </span>
              <span>{choice}</span>
            </button>
          );
        })}
      </div>
      {answered ? (
        <div className="banner banner--info jp" style={{ marginTop: 12 }} role="note">
          <span className="b-ico" aria-hidden="true">
            {correct ? <Icon name="check" size={16} /> : <Icon name="info" size={16} />}
          </span>
          <span className="grow">
            <b>{correct ? "正解" : "不正解"}:</b> {question.explanation}
            {!correct && question.stepSeq != null && onReview ? (
              <>
                {" "}
                <button
                  type="button"
                  onClick={() => onReview(question.stepSeq!)}
                  className="mono"
                  style={{ color: "var(--info-ink)", textDecoration: "underline", textUnderlineOffset: 3, fontSize: 12 }}
                >
                  手順{question.stepSeq}を見直す
                </button>
              </>
            ) : null}
          </span>
        </div>
      ) : null}
    </Card>
  );
}
